import { z } from 'zod';

const uuid = z.string().uuid().transform((value) => value.toLowerCase());
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida (YYYY-MM-DD)');
const count = z.number().int().nonnegative();

/**
 * Autonomous copy of a competition with the common context: a new draft for a
 * new validity. Explicit options decide what is carried over; ledger, states,
 * payouts, closures, acknowledgments and recalculation intents are never copied.
 */
export const commissioningCompetitionCopyInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  period_start: isoDate,
  period_end: isoDate,
  include: z.object({
    /** Active roster, store constraints and the economic subjects of the copy. */
    participants: z.boolean(),
    /** Classifications per configurator, memberships and their assignments. */
    classification: z.boolean(),
    /** TGT, personal targets and the TGT/pay matrix of the assignments. */
    values: z.boolean(),
  }).strict(),
}).strict().superRefine((value, ctx) => {
  if (value.period_end < value.period_start) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['period_end'], message: 'period_end deve essere >= period_start' });
  }
  if (value.include.classification && !value.include.participants) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['include', 'classification'], message: 'La classificazione richiede i partecipanti' });
  }
  if (value.include.values && !value.include.classification) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['include', 'values'], message: 'I valori richiedono la classificazione' });
  }
});
export type CommissioningCompetitionCopyInput = z.infer<typeof commissioningCompetitionCopyInputSchema>;

export const COMMISSIONING_COPY_REVIEW_KINDS = [
  /** Configurator dates outside the new validity were cleared, never shifted. */
  'instance_dates_reset',
  /** Closing periods belong to the old validity: define them again. */
  'periods_not_copied',
  /** The letter is a document of the original competition. */
  'letter_not_copied',
  /** Membership of a participant no longer active: nothing is re-enrolled silently. */
  'member_without_participant',
  /** A catalogue variable still reads the ORIGINAL competition: explicit revision required. */
  'cross_ref_to_original',
] as const;
export type CommissioningCopyReviewKind = typeof COMMISSIONING_COPY_REVIEW_KINDS[number];

export const commissioningCompetitionCopyReviewItemSchema = z.object({
  kind: z.enum(COMMISSIONING_COPY_REVIEW_KINDS),
  instance_id: uuid.nullable(),
  instance_name: z.string().nullable(),
  /** Variable code, period label or other human reference of the item. */
  reference: z.string().nullable(),
  message: z.string().min(1),
}).strict();
export type CommissioningCompetitionCopyReviewItem = z.infer<typeof commissioningCompetitionCopyReviewItemSchema>;

export const commissioningCompetitionCopyReportSchema = z.object({
  source_competition_id: uuid,
  competition_id: uuid,
  copied: z.object({
    participants: count, instances: count, clusters: count, members: count, assignments: count,
    assignment_drivers: count, member_targets: count, interactions: count, functions: count, source_drivers: count,
    /** VP della gara riportati sulla copia (il VP di ogni configuratore viaggia con l'istanza). */
    value_packages: count,
  }).strict(),
  review: z.array(commissioningCompetitionCopyReviewItemSchema),
  /** Outcome of the ordinary preflight on the copy; the draft exists either way. */
  preflight: z.object({ can_publish: z.boolean(), error_count: count, warning_count: count }).strict(),
}).strict();
export type CommissioningCompetitionCopyReport = z.infer<typeof commissioningCompetitionCopyReportSchema>;
